import React, { useMemo, useState } from "react";
import MainContainer from "components/MainContainer";
import { Platform, Pressable, StyleSheet, View } from "react-native";
import useCurrentUser from "hooks/useCurrentUser";
import { Text } from "@ui-kitten/components";
import { AccountScreenProps } from "navigation/types";
import { FontAwesome5, MaterialCommunityIcons } from "@expo/vector-icons";
import BaseCard, { BaseCardListItem } from "../../components/BaseCard";
import Chevron from "components/Chevron";
import UserInfo from "./components/UserInfo";
import * as Linking from "expo-linking";
import Spacer from "components/Spacer";
import { useAccount } from "hooks/useAccount";
import { useLogout } from "hooks/useLogout";
import useIsRunningInBallpark from "hooks/useIsRunningInBallpark";
import AgreementText from "components/AgreementText";
import { useFetchUserMembershipsQuery } from "queries/user.queries";
import { useQueryClient } from "@tanstack/react-query";
import i18n from "plugins/localization";
import { useFetchGiftHistoryQuery } from "queries/account.queries";
import { useGetAutoReserveJobDetailQuery } from "queries/job.queries";
import { JobDetails } from "features/types";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import VerifyPhonePopup from "screens/Dashboard/components/VerifyPhonePopup";
import {
  filterIneligibleSubscriptions,
  getUniqSubscriptions,
} from "utils/subscriptionUtils";

export const CreditCardClock = () => (
  <MaterialCommunityIcons
    name="credit-card-clock-outline"
    size={24}
    color="black"
  />
);

const AutoReserveStatus = ({
  status = "SUBMITTED",
}: {
  status?: JobDetails["status"];
}) => {
  const statusColor = {
    SUBMITTED: "primary",
    PROCESSING: "info",
    COMPLETED: "success",
    FAILED: "danger",
  };
  return <Text status={statusColor[status]}>{status}</Text>;
};

export default function Account({ navigation }: AccountScreenProps) {
  // hooks
  const { logoutAccount, resetState } = useLogout();
  const { account } = useAccount();
  const { isRunningInBallpark } = useIsRunningInBallpark();
  const { currentUser, userImageURL, currentMember } = useCurrentUser();
  // const theme = useAppSelector((state) => state.theme.theme);

  // react-query
  const queryClient = useQueryClient();
  const { data: userMemberships } = useFetchUserMembershipsQuery();
  const { data: gifts } = useFetchGiftHistoryQuery();
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();

  const [verifyPhonePopupVisible, setVerifyPhonePopupVisible] = useState(false);
  const accountHasAutoReservePlan = useMemo(() => {
    if (!account) return false;
    return getUniqSubscriptions(account.subscriptions).some(
      (sub) => sub.planPackage.autoReserveEnabled
    );
  }, [account?.subscriptions]);

  const { data: autoReserveJobDetail, isError } =
    useGetAutoReserveJobDetailQuery({
      enabled: accountHasAutoReservePlan,
      userId: currentUser?.id,
    });

  const isFullAccount =
    account?.type === "FULL" ||
    account?.subscriptions.some(
      (sub) => filterIneligibleSubscriptions(sub) && sub.status === "ACTIVE"
    );

  const handleSwitchMembership = () => {
    // clears query cache and resets redux state
    queryClient.removeQueries(["fetchTenantBranding"]);
    resetState();
  };

  if (!currentMember) return null;

  return (
    <MainContainer title="Account" showLeftAction={false} grayBackground>
      <View style={{ padding: 15 }}>
        {/* account details card */}
        <BaseCard>
          <View>
            {currentUser && (
              <UserInfo
                userImageURL={userImageURL}
                currentMember={currentMember}
              />
            )}
          </View>
          {!isRunningInBallpark && (
            <BaseCardListItem
              title={i18n.t("screens.Account.accountDetails")}
              onPress={() =>
                navigation.navigate("AccountDetails", {
                  screen: "AccountDetailsScreen",
                })
              }
            />
          )}
          <BaseCardListItem
            title={i18n.t("screens.Account.yourActivity")}
            onPress={() => navigation.navigate("Activity")}
          />
          {isFullAccount && (
            <BaseCardListItem
              title={i18n.t("screens.Account.memberCard")}
              onPress={() => navigation.navigate("MemberCard")}
            />
          )}
          {!currentUser?.phoneVerified && (
            <BaseCardListItem
              title="Verify Phone Number"
              onPress={() => setVerifyPhonePopupVisible(true)}
            />
          )}
        </BaseCard>

        <VerifyPhonePopup
          visible={verifyPhonePopupVisible}
          onCancel={() => setVerifyPhonePopupVisible(false)}
        />
        {/* total balance */}
        {/* <Spacer /> */}
        {/* <BaseCard title="Total Balance">
          <Text category="h3" style={{ marginTop: 10 }}>
            $149.99
          </Text>
          <BaseCardListItem
            title="Autopay scheduled for April 10"
            accessoryLeft={<CreditCardClock />}
            showArrow={false}
          ></BaseCardListItem>
        </BaseCard> */}

        {/* your subscriptions */}
        {tenantConfig?.disableAccountFunctions === false && (
          <>
            <Spacer />

            <BaseCard title={i18n.t("screens.Account.yourPlans")}>
              <BaseCardListItem
                title={i18n.t("screens.Account.managePlansAndServices")}
                onPress={() =>
                  navigation.navigate("ManageSubscriptions", {
                    screen: "ManageSubscriptionsScreen",
                  })
                }
              ></BaseCardListItem>
              {isFullAccount && (
                <BaseCardListItem
                  title={i18n.t("screens.Account.addNewMember")}
                  onPress={() => navigation.navigate("AddMembership")}
                ></BaseCardListItem>
              )}

              {gifts && gifts.length > 0 && (
                <BaseCardListItem
                  title={"My Gifts"}
                  onPress={() => navigation.navigate("MyGifts")}
                ></BaseCardListItem>
              )}

              {(autoReserveJobDetail || isError) && (
                <BaseCardListItem
                  title="Auto Reserve Status"
                  accessoryRight={
                    <AutoReserveStatus status={autoReserveJobDetail?.status} />
                  }
                ></BaseCardListItem>
              )}
            </BaseCard>
          </>
        )}

        <Spacer />
        {/* payment method */}
        <BaseCard title={i18n.t("screens.Account.billing")}>
          <BaseCardListItem
            title={i18n.t("screens.Account.updatePaymentMethod")}
            accessoryLeft={
              <MaterialCommunityIcons name="credit-card-outline" size={24} />
            }
            onPress={() => navigation.navigate("PaymentMethod")}
          ></BaseCardListItem>
        </BaseCard>

        <Spacer />
        {/* general settings */}
        <BaseCard title={i18n.t("screens.Account.generalSettings")}>
          <BaseCardListItem
            title={i18n.t("screens.Account.updateSettings")}
            accessoryLeft={
              <MaterialCommunityIcons
                name="cog-outline"
                size={24}
                color="black"
              />
            }
            onPress={() =>
              navigation.navigate("Settings", { screen: "SettingScreen" })
            }
          ></BaseCardListItem>
        </BaseCard>

        <Spacer />
        {/* support resources */}
        <BaseCard title={i18n.t("screens.Account.supportResources")}>
          <BaseCardListItem
            title={`${i18n.t("screens.Account.phone")}: ${
              tenantConfig?.supportPhone
            }`}
            showArrow={false}
            accessoryLeft={
              <MaterialCommunityIcons
                name="phone-outline"
                size={24}
                color="black"
              />
            }
            onPress={() => Linking.openURL(`tel:${tenantConfig?.supportPhone}`)}
          ></BaseCardListItem>

          <BaseCardListItem
            title={`${i18n.t("screens.Account.email")}: ${
              tenantConfig?.supportEmail
            }`}
            showArrow={false}
            accessoryLeft={
              <MaterialCommunityIcons
                name="email-outline"
                size={24}
                color="black"
              />
            }
            onPress={() =>
              Linking.openURL(`mailto:${tenantConfig?.supportEmail}`)
            }
          ></BaseCardListItem>
          {!isRunningInBallpark && (
            <BaseCardListItem
              title="Customer Feedback"
              accessoryLeft={
                <FontAwesome5 name="comment-dots" size={24} color="black" />
              }
              onPress={() =>
                Linking.openURL("https://www.surveymonkey.com/r/3TYCWPJ")
              }
            ></BaseCardListItem>
          )}
        </BaseCard>

        <Spacer />
        {/* privacy */}

        <AgreementText visibility="ACCOUNT_PAGE" variant="CARD" />

        <Spacer />
        {!isRunningInBallpark && (
          <Pressable onPress={logoutAccount} testID="log-out-button">
            <BaseCard>
              <View style={styles.signoutButton}>
                <Text>{i18n.t("screens.Account.signOut")}</Text>
                <Chevron></Chevron>
              </View>
            </BaseCard>
          </Pressable>
        )}

        {!isFullAccount && (
          <>
            <Spacer />
            <Pressable
              onPress={() =>
                navigation.navigate("ManageSubscriptions", {
                  screen: "SubscriptionDetail",
                  params: {
                    memberId: String(currentMember.id),
                  },
                })
              }
            >
              <BaseCard>
                <View style={styles.signoutButton}>
                  <Text>{i18n.t("screens.Account.upgradeToFullAccount")}</Text>
                  <Chevron></Chevron>
                </View>
              </BaseCard>
            </Pressable>
          </>
        )}

        {userMemberships &&
          userMemberships.length > 1 &&
          Platform.OS !== "web" && (
            <>
              <Spacer />
              <Pressable
                onPress={() =>
                  navigation.navigate("ManageSubscriptions", {
                    screen: "SubscriptionDetail",
                    params: {
                      memberId: String(currentMember.id),
                    },
                  })
                }
              >
                <BaseCard>
                  <View style={styles.signoutButton}>
                    <Text>{i18n.t("screens.Account.switchMembership")}</Text>
                    <Chevron></Chevron>
                  </View>
                </BaseCard>
              </Pressable>
            </>
          )}
      </View>
      <View>
        {userMemberships &&
          userMemberships.length > 1 &&
          Platform.OS !== "web" && (
            <>
              <Spacer />
              <Pressable onPress={handleSwitchMembership}>
                <BaseCard>
                  <View style={styles.signoutButton}>
                    <Text>{i18n.t("screens.Account.switchMembership")}</Text>
                    <Chevron></Chevron>
                  </View>
                </BaseCard>
              </Pressable>
            </>
          )}
      </View>

      {!isRunningInBallpark && (
        <View style={{ padding: 15, marginTop: 30 }}>
          <Text style={{ color: "rgb(208, 208, 208)" }}>
            Made with ❤ at FanRally
          </Text>
        </View>
      )}
    </MainContainer>
  );
}

const styles = StyleSheet.create({
  signoutButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
});
